import { Injectable, inject } from '@angular/core';
import { Metadata, staticPages } from 'src/app/data/seo.data';
import { Meta } from '@angular/platform-browser';
import { CatalogProduct, CatalogProductType } from 'src/app/models/portal.model';
import { PortalService } from '../portal/portal.service';
import { UrlSegment } from '@angular/router';


@Injectable({
  providedIn: 'root'
})

export class SeoService {
  private metaService = inject(Meta);
  private portalService = inject(PortalService);


	// Generate the <meta> for static page based on the seo.data.ts file
	public getStaticPageMetaTags(pageId: any) {
		if(!pageId) return;

		const obj = staticPages[pageId];

		if(obj) {
			this.generateMetaTag(obj);
		} else {
			// <meta name="robots" content="noindex, nofollow">
			// return this.metaService.updateTag({ name: "robots", content: "noindex, nofollow" });

			// DELETE meta tags from previous route
			this.removeMetaTags();
		}
	}

	// Generate <meta> for dynamic product page based on the product deatil info
	public async generateSingleProdPageMetaTags(catalogType: CatalogProductType, productId: string) {
		if(!catalogType || !productId) return;

		await this.portalService.getCatalogProductsByType(catalogType).then(async catalogProducts => {
            // Find product based on the productId on route
            const catalogProduct: CatalogProduct | undefined = catalogProducts.find(x => x.id === productId);

			if(catalogProduct) {
				const obj: Metadata = Object.assign(new Metadata(), {
					"title": catalogProduct.name,
					"description": catalogProduct.description.short,
					"og:title": catalogProduct.name,
					"og:description": catalogProduct.description.short,
					"og:url": location.href,
					"og:image": catalogProduct.image.thumbnail,
					"og:image:secure_url": catalogProduct.image.thumbnail,
					"og:image:alt": catalogProduct.name + " image",
					"twitter:title": catalogProduct.name,
					"twitter:description": catalogProduct.description.short,
					"twitter:image": catalogProduct.image.thumbnail
				});

				this.generateMetaTag(obj);
			} else {
				// DELETE meta tags from previous route
				this.removeMetaTags();
			}
		});
	}

	// Delete <meta> tags from previous page when navigating to a route that does not included any reference 'static' or 'dynamic' data to be created
	// if we need to delete a specific metaTag we should use the param with the following value => "name='og:title'"
	public deletePageMetaTags(metaTag?: string) {
		this.removeMetaTags(metaTag);
	}

	//  Generate a dynamic canonical link tag
	public generateCanonicalURL(url: UrlSegment[]) {
		this.setCanonicalURL(url);
	}



	/**
	 *
	 * PRIVATE
	 */

	// Generate tag
	private generateMetaTag(obj: Metadata) {
		Object.entries(obj).forEach(([key, value]) => {
			value = typeof value === 'string' ? value : JSON.stringify(value);

			if(key.indexOf("og:") !== -1) {
				this.metaService.updateTag({ property: `${key}`, content: `${value}`});
			} else {
				this.metaService.updateTag({ name: `${key}`, content: `${value}`});
			}
		});
	}

	// Delete tag
	private removeMetaTags(metaTag?: string) {
		if(metaTag) {
			this.metaService.removeTag(metaTag);
		} else {
			const metaTags = document.querySelectorAll("meta"); // Get all meta tags
			const metadataKeys = Object.getOwnPropertyNames(new Metadata());

			// Iterate over the DOM meta tags
			metaTags.forEach((metaTag) => {
				const property = metaTag.getAttribute('property') || metaTag.getAttribute('name'); // Either 'property' (for OG) or 'name' (for others)

				if (property && metadataKeys.includes(property)) {
					this.metaService.removeTag(`name='${property}'`);
				}
			});
		}
	}

	// Create the canonical tag
	private setCanonicalURL(url: UrlSegment[]) {
		// Construct your canonical URL based on the route or other criteria
		const baseUrl = location.origin;
		const path = url.map(segment => segment.path).join('/');
		const canonicalUrl = `${baseUrl}/${path}`;

		const linkElement = document.createElement('link');
		linkElement.setAttribute('rel', 'canonical');
		linkElement.setAttribute('href', canonicalUrl);

		// Remove any existing canonical link tags to avoid duplicates
		const existingLink = document.querySelector('link[rel="canonical"]');
		if (existingLink) {
			document.head.removeChild(existingLink);
		}

		// Append the new canonical link to the head
		document.head.appendChild(linkElement);
	}
}
